import type {
  Recipe,
  RecipeIngredient,
  MealPlan,
  MealPlanIngredient,
  MealPlanAttendee,
  MealStatus,
  AttendanceStatus,
} from '../types/meal.js';
import { formatLocalDate } from '../lib/dateUtils.js';

/**
 * Central mapping layer between Supabase rows (snake_case) and frontend meal models.
 * Follows the same `'key' in updates` pattern as stockMapper / purchaseMapper so that
 * partial updates can distinguish "omitted" from "set to null".
 *
 * DATE columns (plan dates, deleted_at) use formatLocalDate (YYYY-MM-DD) to avoid TZ drift.
 * TIMESTAMPTZ columns (created_at, cooked_at) use .toISOString().
 */

// ───────────────── Recipe ─────────────────

// Explicit column allowlist — keep in sync with dbRowToRecipe. Using this
// with `.select(...)` instead of `.select('*')` prevents future internal /
// owner-only columns added to `recipes` (e.g. AI-suggestion provenance,
// nutrition scoring, premium-tier flags) from auto-flowing to clients.
export const RECIPE_COLUMNS = [
  'id',
  'team_id',
  'name',
  'notes',
  'image_url',
  'servings',
  'prep_minutes',
  'cook_minutes',
  'effort',
  'tag_ids',
  'metadata',
  'source',
  'created_by_member_id',
  'created_at',
  'updated_at',
  'deleted_at',
].join(', ');

export const dbRowToRecipe = (row: any): Recipe => ({
  id: row.id,
  teamId: row.team_id,
  name: row.name,
  notes: row.notes ?? null,
  imageUrl: row.image_url ?? null,
  servings: row.servings ?? 2,
  prepMinutes: row.prep_minutes ?? null,
  cookMinutes: row.cook_minutes ?? null,
  effort: row.effort ?? null,
  tagIds: row.tag_ids ?? [],
  metadata: row.metadata ?? {},
  source: row.source ?? null,
  createdByMemberId: row.created_by_member_id ?? null,
  createdAt: new Date(row.created_at),
  updatedAt: new Date(row.updated_at),
  deletedAt: row.deleted_at ? new Date(row.deleted_at) : null,
  ingredients: [],
});

export const mapRecipeToDb = (updates: Partial<Recipe>): any => {
  const db: any = {};
  if ('teamId' in updates) db.team_id = updates.teamId;
  if ('name' in updates) db.name = updates.name;
  if ('notes' in updates) db.notes = updates.notes ?? null;
  if ('imageUrl' in updates) db.image_url = updates.imageUrl ?? null;
  if ('servings' in updates) db.servings = updates.servings;
  if ('prepMinutes' in updates) db.prep_minutes = updates.prepMinutes ?? null;
  if ('cookMinutes' in updates) db.cook_minutes = updates.cookMinutes ?? null;
  if ('effort' in updates) db.effort = updates.effort ?? null;
  if ('tagIds' in updates) db.tag_ids = updates.tagIds;
  if ('metadata' in updates) db.metadata = updates.metadata;
  if ('source' in updates) db.source = updates.source ?? null;
  if ('createdByMemberId' in updates) db.created_by_member_id = updates.createdByMemberId ?? null;
  if ('deletedAt' in updates) {
    db.deleted_at = updates.deletedAt instanceof Date
      ? updates.deletedAt.toISOString()
      : updates.deletedAt ?? null;
  }
  return db;
};

// ───────────────── RecipeIngredient ─────────────────

// Explicit column allowlist — keep in sync with dbRowToRecipeIngredient.
export const RECIPE_INGREDIENT_COLUMNS = [
  'id',
  'recipe_id',
  'stock_item_id',
  'freeform_name',
  'quantity',
  'unit',
  'optional',
  'sort_order',
  'created_at',
].join(', ');

export const dbRowToRecipeIngredient = (row: any): RecipeIngredient => ({
  id: row.id,
  recipeId: row.recipe_id,
  stockItemId: row.stock_item_id ?? null,
  freeformName: row.freeform_name ?? null,
  quantity: Number(row.quantity ?? 1),
  unit: row.unit ?? 'stuks',
  optional: row.optional ?? false,
  sortOrder: row.sort_order ?? 0,
  createdAt: new Date(row.created_at),
});

export const mapRecipeIngredientToDb = (updates: Partial<RecipeIngredient>): any => {
  const db: any = {};
  if ('recipeId' in updates) db.recipe_id = updates.recipeId;
  if ('stockItemId' in updates) db.stock_item_id = updates.stockItemId ?? null;
  if ('freeformName' in updates) db.freeform_name = updates.freeformName ?? null;
  if ('quantity' in updates) db.quantity = updates.quantity;
  if ('unit' in updates) db.unit = updates.unit;
  if ('optional' in updates) db.optional = updates.optional;
  if ('sortOrder' in updates) db.sort_order = updates.sortOrder;
  return db;
};

// ───────────────── MealPlan ─────────────────

// Explicit column allowlist — keep in sync with dbRowToMealPlan. Using this
// with `.select(...)` instead of `.select('*')` prevents future internal /
// owner-only columns added to `meal_plans` from auto-flowing to clients.
export const MEAL_PLAN_COLUMNS = [
  'id',
  'team_id',
  'planned_date',
  'recipe_id',
  'name',
  'servings',
  'notes',
  'status',
  'cooked_at',
  'cooked_by_member_id',
  'created_by_member_id',
  'created_at',
  'updated_at',
  'deleted_at',
].join(', ');

export const dbRowToMealPlan = (row: any): MealPlan => ({
  id: row.id,
  teamId: row.team_id,
  plannedDate: new Date(row.planned_date),
  recipeId: row.recipe_id ?? null,
  name: row.name,
  servings: row.servings ?? 2,
  notes: row.notes ?? null,
  status: (row.status ?? 'planned') as MealStatus,
  cookedAt: row.cooked_at ? new Date(row.cooked_at) : null,
  cookedByMemberId: row.cooked_by_member_id ?? null,
  createdByMemberId: row.created_by_member_id ?? null,
  createdAt: new Date(row.created_at),
  updatedAt: new Date(row.updated_at),
  deletedAt: row.deleted_at ? new Date(row.deleted_at) : null,
  ingredients: [],
  attendees: [],
});

export const mapMealPlanToDb = (updates: Partial<MealPlan>): any => {
  const db: any = {};
  if ('teamId' in updates) db.team_id = updates.teamId;
  if ('plannedDate' in updates) {
    db.planned_date = updates.plannedDate instanceof Date
      ? formatLocalDate(updates.plannedDate)
      : updates.plannedDate;
  }
  if ('recipeId' in updates) db.recipe_id = updates.recipeId ?? null;
  if ('name' in updates) db.name = updates.name;
  if ('servings' in updates) db.servings = updates.servings;
  if ('notes' in updates) db.notes = updates.notes ?? null;
  if ('status' in updates) db.status = updates.status;
  if ('cookedAt' in updates) {
    db.cooked_at = updates.cookedAt instanceof Date
      ? updates.cookedAt.toISOString()
      : updates.cookedAt ?? null;
  }
  if ('cookedByMemberId' in updates) db.cooked_by_member_id = updates.cookedByMemberId ?? null;
  if ('createdByMemberId' in updates) db.created_by_member_id = updates.createdByMemberId ?? null;
  if ('deletedAt' in updates) {
    db.deleted_at = updates.deletedAt instanceof Date
      ? updates.deletedAt.toISOString()
      : updates.deletedAt ?? null;
  }
  return db;
};

// ───────────────── MealPlanIngredient ─────────────────

// Explicit column allowlist — keep in sync with dbRowToMealPlanIngredient.
export const MEAL_PLAN_INGREDIENT_COLUMNS = [
  'id',
  'meal_plan_id',
  'stock_item_id',
  'freeform_name',
  'quantity',
  'unit',
  'optional',
  'sort_order',
  'needed_from_stock',
  'needed_to_buy',
  'created_at',
].join(', ');

export const dbRowToMealPlanIngredient = (row: any): MealPlanIngredient => ({
  id: row.id,
  mealPlanId: row.meal_plan_id,
  stockItemId: row.stock_item_id ?? null,
  freeformName: row.freeform_name ?? null,
  quantity: Number(row.quantity ?? 1),
  unit: row.unit ?? 'stuks',
  optional: row.optional ?? false,
  sortOrder: row.sort_order ?? 0,
  neededFromStock: Number(row.needed_from_stock ?? 0),
  neededToBuy: Number(row.needed_to_buy ?? 0),
  createdAt: new Date(row.created_at),
});

export const mapMealPlanIngredientToDb = (updates: Partial<MealPlanIngredient>): any => {
  const db: any = {};
  if ('mealPlanId' in updates) db.meal_plan_id = updates.mealPlanId;
  if ('stockItemId' in updates) db.stock_item_id = updates.stockItemId ?? null;
  if ('freeformName' in updates) db.freeform_name = updates.freeformName ?? null;
  if ('quantity' in updates) db.quantity = updates.quantity;
  if ('unit' in updates) db.unit = updates.unit;
  if ('optional' in updates) db.optional = updates.optional;
  if ('sortOrder' in updates) db.sort_order = updates.sortOrder;
  if ('neededFromStock' in updates) db.needed_from_stock = updates.neededFromStock;
  if ('neededToBuy' in updates) db.needed_to_buy = updates.neededToBuy;
  return db;
};

// ───────────────── MealPlanAttendee ─────────────────

// Explicit column allowlist — keep in sync with dbRowToMealPlanAttendee.
export const MEAL_PLAN_ATTENDEE_COLUMNS = [
  'id',
  'meal_plan_id',
  'member_id',
  'status',
  'notes',
  'responded_at',
  'created_at',
  'updated_at',
].join(', ');

export const dbRowToMealPlanAttendee = (row: any): MealPlanAttendee => ({
  id: row.id,
  mealPlanId: row.meal_plan_id,
  memberId: row.member_id,
  status: (row.status ?? 'attending') as AttendanceStatus,
  notes: row.notes ?? null,
  respondedAt: new Date(row.responded_at ?? row.created_at),
  createdAt: new Date(row.created_at),
  updatedAt: new Date(row.updated_at ?? row.created_at),
});

export const mapMealPlanAttendeeToDb = (updates: Partial<MealPlanAttendee>): any => {
  const db: any = {};
  if ('mealPlanId' in updates) db.meal_plan_id = updates.mealPlanId;
  if ('memberId' in updates) db.member_id = updates.memberId;
  if ('status' in updates) db.status = updates.status;
  if ('notes' in updates) db.notes = updates.notes ?? null;
  if ('respondedAt' in updates) {
    db.responded_at = updates.respondedAt instanceof Date
      ? updates.respondedAt.toISOString()
      : updates.respondedAt;
  }
  return db;
};
